import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  LogOut,
  Settings,
  Box,
  ScrollText,
  Users,
  UserCircle2,
  Gamepad2,
  BadgeDollarSign,
  Wallet,
} from "lucide-react";
import logotexto from "../assets/logotexto.png";
import { useAuth } from "../context/AuthContext";
import { Joystick } from "lucide-react";
import  WalletModal  from './shop/WalletModal';  // Ajusta la ruta según tu estructura
import { useWebSocket } from "../context/WebSocketContext";


const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showWalletDetails, setShowWalletDetails] = useState(false);
  const { logout, user: userData } = useAuth();
  const navigate = useNavigate();
  const { isReconnecting } = useWebSocket();
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const hasGamePermissions = userData?.dataUser?.is_venta_juego == 1;
  
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleLogoClick = () => {
    window.location.reload();
  };
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const WalletButton = () => (
    <button onClick={() => setShowWalletDetails(true)} className="group relative">
      <div className="absolute inset-0 rounded-full bg-yellow-300 blur-sm transform group-hover:scale-110 transition-transform duration-200"></div>
      <div className="relative flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-b from-yellow-300 to-yellow-500 transform active:scale-95 transition-all duration-150 shadow-lg border-2 border-yellow-200">
        <div className="absolute inset-1 rounded-full bg-gradient-to-b from-yellow-200 to-yellow-400"></div>
        <Wallet className="relative w-5 h-5 text-red-900 transform group-hover:scale-110 transition-transform duration-200" />
      </div>
    </button>
  );
 

  return (
    <>
      <nav className="fixed top-0 z-[50] bg-gradient-to-r from-red-950 via-red-900 to-red-950 shadow-lg w-full text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 relative">
            {isReconnecting && (
              <div className="absolute inset-x-0 top-2 flex justify-center pointer-events-none">
                <div className="flex items-center gap-2 rounded-full bg-yellow-500/20 px-3 py-1 text-xs text-yellow-100 shadow-sm">
                  <span className="h-2 w-2 rounded-full bg-yellow-300 animate-pulse" />
                  <span>Reconectando…</span>
                </div>
              </div>
            )}
            {/* Botón de menú - visible hasta lg */}
            <div className="  absolute left-0">
              <button
                ref={buttonRef}
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-primary-foreground hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white transition-colors duration-200"
                aria-expanded={isOpen}
              >
                {isOpen ? (
                  <X className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Menu className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>

            {/* Logo */}
            <div className="flex-1 flex items-center justify-center  t">
              <button
                type="button"
                onClick={handleLogoClick}
                className="flex-shrink-0 focus:outline-none"
              >
                <img src={logotexto} alt="Logo" className="h-8 w-auto" />
              </button>
            </div>

            <div className="  absolute right-0">
              <WalletButton />
            </div>

            {/* Wallet button and desktop menu */}
            <div className="flex items-center space-x-4">
              <div className="hidden  ">
                <div className="ml-10 flex items-center space-x-4">
                  <Link
                    to="/caja"
                    className="text-white hover:bg-white/10 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center gap-2"
                  >
                    <Box className="w-4 h-4" />
                    Caja
                  </Link>
                  <Link
                    to="/movimientos/cajas"
                    className="text-white hover:bg-white/10 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center gap-2"
                  >
                    <ScrollText className="w-4 h-4" />
                    Movimientos de caja
                  </Link>

                  {hasGamePermissions && (
                    <>
                      <Link
                        to="/shop/ventas"
                        className="text-white hover:bg-white/10 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center gap-2"
                      >
                        <BadgeDollarSign className="w-4 h-4" />
                        Ventas
                      </Link>
                      <Link
                        to="/shop"
                        className="text-white hover:bg-white/10 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center gap-2"
                      >
                        <Gamepad2 className="w-4 h-4" />
                        Game Shop
                      </Link>
                    </>
                  )}
                  <Link
                    to="/movimientos/clientes"
                    className="text-white hover:bg-white/10 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center gap-2"
                  >
                    <Users className="w-4 h-4" />
                    Movimientos de clientes
                  </Link>

                  <Link
                    to="/maquinas/lista"
                    className="text-white hover:bg-white/10 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center gap-2"
                  >
                    <Joystick className="w-4 h-4" />
                    Lista de Máquinas
                  </Link>

                  <Link
                    to="/configuracion"
                    className="text-white hover:bg-white/10 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    Configuración
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="text-white hover:bg-white/10 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          ref={menuRef}
          className={`fixed inset-y-0 left-0 w-64 bg-gradient-to-b z-[52] from-red-950 to-red-900 transform transition-all duration-300 ease-in-out  shadow-xl ${
            isOpen ? "translate-x-0" : "-translate-x-full"
          }`}
          style={{ top: "64px" }}
        >
          {/* User info */}
          <div className="p-4 border-b border-red-800/50 bg-black/20">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-red-800 to-red-900 rounded-full flex items-center justify-center shadow-inner">
                <UserCircle2 className="w-8 h-8 text-white" />
              </div>
              <div className="flex flex-col">
                <p className="text-white font-semibold">
                  {userData?.dataUser?.nombre} {userData?.dataUser?.apellido}
                </p>
                <p className="text-sm text-gray-300 truncate max-w-[180px]">
                  {userData?.dataUser?.nombre_local}
                </p>
              </div>
            </div>
          </div>

          {/* Menu items */}
          <div className="py-2">
            {/* Add Wallet option in mobile menu */}
            <button
              onClick={() => {
                setShowWalletDetails(true);
                setIsOpen(false);
              }}
              className="w-full flex items-center px-4 py-3 text-white hover:bg-white/5 active:bg-white/10 transition-colors duration-200"
            >
              <Wallet className="w-5 h-5 mr-3 text-yellow-400" />
              <span className="flex-1">Mi Billetera</span>
            </button>

            <Link
              to="/caja"
              className="flex items-center px-4 py-3 text-white hover:bg-white/5 active:bg-white/10 transition-colors duration-200"
              onClick={toggleMenu}
            >
              <Box className="w-5 h-5 mr-3 text-gray-300" />
              <span className="flex-1">Caja</span>
            </Link>

            <Link
              to="/movimientos/cajas"
              className="flex items-center px-4 py-3 text-white hover:bg-white/5 active:bg-white/10 transition-colors duration-200"
              onClick={toggleMenu}
            >
              <ScrollText className="w-5 h-5 mr-3 text-gray-300" />
              <span className="flex-1">Movimiento caja</span>
            </Link>

            {hasGamePermissions && (
              <>
                <Link
                  to="/shop/ventas"
                  className="flex items-center px-4 py-3 text-white hover:bg-white/5 active:bg-white/10 transition-colors duration-200"
                  onClick={toggleMenu}
                >
                  <BadgeDollarSign className="w-5 h-5 mr-3 text-gray-300" />
                  <span className="flex-1">Ventas</span>
                </Link>
                <Link
                  to="/shop"
                  className="flex items-center px-4 py-3 text-white hover:bg-white/5 active:bg-white/10 transition-colors duration-200"
                  onClick={toggleMenu}
                >
                  <Gamepad2 className="w-5 h-5 mr-3 text-gray-300" />
                  <span className="flex-1">Tienda de Juegos</span>
                </Link>
              </>
            )}

            <Link
              to="/movimientos/clientes"
              className="flex items-center px-4 py-3 text-white hover:bg-white/5 active:bg-white/10 transition-colors duration-200"
              onClick={toggleMenu}
            >
              <Users className="w-5 h-5 mr-3 text-gray-300" />
              <span className="flex-1">Movimiento cliente</span>
            </Link>

            <Link
              to="/maquinas/lista"
              className="flex items-center px-4 py-3 text-white hover:bg-white/5 active:bg-white/10 transition-colors duration-200"
            >
              <Joystick className="w-5 h-5 mr-3 text-gray-300" />
              Lista de Máquinas
            </Link>

            <Link
              to="/configuracion"
              className="flex items-center px-4 py-3 text-white hover:bg-white/5 active:bg-white/10 transition-colors duration-200"
              onClick={toggleMenu}
            >
              <Settings className="w-5 h-5 mr-3 text-gray-300" />
              <span className="flex-1">Configuración</span>
            </Link>

            <div className="relative py-4">
              <div className="absolute inset-0 flex items-center px-4">
                <div className="w-full border-t border-gray-300/20 shadow-sm"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="bg-gradient-to-r from-red-950 via-red-900 to-red-950 px-4">
                  <div className="w-2 h-2 rounded-full bg-red-500/50"></div>
                </span>
              </div>
            </div>

            <button
              onClick={handleLogout}
              className="w-full flex items-center px-4 py-3 text-white hover:bg-white/5 active:bg-white/10 transition-colors duration-200"
            >
              <LogOut className="w-5 h-5 mr-3 text-gray-300" />
              <span className="flex-1">Cerrar Sesión</span>
            </button>
          </div>
        </div>

        {/* Overlay */}
        <div
          className={`fixed inset-0 bg-black/50 transition-opacity duration-300 ease-in-out   ${
            isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          style={{ top: "64px" }}
        />
      </nav>

      {/* Wallet Modal */}
      {showWalletDetails && (
        <WalletModal setShowWalletDetails={setShowWalletDetails} />
      )}
    </>
  );
};

export default Navbar;
